import { normalizeSearchText, suggest } from './search-source.js';
import { openSourcingRequest } from './search-sourcing.js';

const RECENT_KEY = 'ptt-recent-searches';
const DEBOUNCE_MS = 200;
const SCOPE_ALL = 'Tất Cả Ngành Hàng';

const readConfig = () => {
  const node = document.querySelector('[data-search-config]');
  if (!node) return {};
  try { return JSON.parse(node.textContent || '{}'); } catch { return {}; }
};

const readRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
};

export const recordSearch = (term = '') => {
  const clean = String(term || '').trim();
  if (!clean) return;
  const list = [clean, ...readRecent().filter((item) => normalizeSearchText(item) !== normalizeSearchText(clean))].slice(0, 6);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch {}
};

const saveRecent = (list) => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6))); } catch {}
};

const el = (tag, className, attrs = {}) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  Object.entries(attrs).forEach(([key, value]) => {
    if (value === false || value === null || value === undefined) return;
    if (key === 'text') node.textContent = value;
    else if (key === 'html') node.innerHTML = value;
    else if (key === 'hidden') node.hidden = Boolean(value);
    else node.setAttribute(key, value);
  });
  return node;
};

const money = (product, key, fallbackKey) => product[fallbackKey] || (Number(product[key] || 0) / 100).toLocaleString('vi-VN') + '₫';
const searchUrl = (config, term) => `${config.routes?.searchUrl || '/search'}?q=${encodeURIComponent(term)}&type=product&options%5Bprefix%5D=last`;
const productStockLabel = (state) => state === 'preorder' ? 'Đặt trước 1-2 tuần' : state === 'sold-out' ? 'Hết hàng' : 'Còn hàng';
const focusableSelector = 'button,input,a';
const isQuoteDialog = (node) => node?.matches?.('[data-quote-dialog]');

const ICON_PATHS = {
  search: '<circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/><path d="m16.5 16.5 4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  history: '<g stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l4 2" /></g>',
  'chevron-right': '<path d="m9 6 6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'file-search': '<g stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h7" /><path d="M14 2v6h6" /><path d="M16.5 17.5 21 22" /><circle cx="15" cy="16" r="3" /></g>',
  'message-circle': '<g stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" /></g>'
};

const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

const icon = (name, className = 'ptt-search-panel__icon') => {
  const aliases = { '⌕': 'search', '↺': 'history', '›': 'chevron-right', '▧': 'file-search' };
  const iconName = aliases[name] || name;
  const node = el('span', className, { 'aria-hidden': 'true' });
  if (ICON_PATHS[iconName]) {
    node.innerHTML = `<svg class="ptt-icon" focusable="false" viewBox="0 0 24 24" fill="none">${ICON_PATHS[iconName]}</svg>`;
  } else {
    node.textContent = name;
  }
  return node;
};

const highlight = (text, query, { strong = false } = {}) => {
  const original = String(text || '');
  const q = normalizeSearchText(query);
  if (!q) return escapeHtml(original);
  const chars = Array.from(original);
  const normalizeChar = (ch) => String(ch).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'd').toLowerCase();
  const normalized = chars.map(normalizeChar).join('');
  const mark = new Array(chars.length).fill(false);
  normalizeSearchText(query).split(/\s+/).filter(Boolean).forEach((token) => {
    let index = normalized.indexOf(token);
    while (index >= 0) {
      for (let i = index; i < index + token.length; i += 1) mark[i] = true;
      index = normalized.indexOf(token, index + token.length);
    }
  });
  let html = '';
  let open = false;
  let spanOpen = false;
  chars.forEach((ch, index) => {
    if (mark[index] && spanOpen) { html += '</span>'; spanOpen = false; }
    if (!mark[index] && open) { html += '</mark>'; open = false; }
    if (mark[index] && !open) { html += strong ? '<mark class="ptt-search-panel__mark--strong">' : '<mark>'; open = true; }
    if (!mark[index] && strong && !spanOpen) { html += '<span class="ptt-search-panel__completion">'; spanOpen = true; }
    html += escapeHtml(ch);
  });
  if (open) html += '</mark>';
  if (spanOpen) html += '</span>';
  return html;
};

class SearchPanel {
  constructor(form) {
    this.form = form;
    this.input = form.querySelector('[data-search-scope-input]');
    this.clear = form.querySelector('[data-search-clear]');
    this.anchor = form.querySelector('[data-search-panel-anchor]');
    this.scopeLabel = form.querySelector('[data-search-scope-label]');
    this.scopeList = form.querySelector('[data-search-scope-list]');
    this.scopeOptions = [...form.querySelectorAll('[data-search-scope-option]')];
    this.config = readConfig();
    this.state = { open: false, sheet: false, query: this.input.value || '', data: null, active: -1, recent: readRecent(), requestCount: 0, renderTimes: [] };
    this.actions = [];
    this.abortController = null;
    this.timer = 0;
    this.pendingStartedAt = 0;
    this.metrics = { requestCount: 0, renderTimes: [] };
    this.inertSiblings = [];
    this.bind();
  }

  bind() {
    this.form.addEventListener('submit', (event) => {
      const term = this.input.value.trim();
      if (term) recordSearch(term);
      if (this.state.sheet) this.close();
    });
    this.input.addEventListener('focus', () => {
      if (this.isPhone()) this.openSheet();
      else this.open();
    });
    this.input.addEventListener('input', () => {
      this.state.query = this.input.value;
      this.clear.hidden = !this.state.query;
      if (this.state.sheet) this.sheetInput.value = this.state.query;
      this.open();
      this.queueSuggest();
    });
    this.input.addEventListener('keydown', (event) => this.keydown(event));
    this.clear.addEventListener('mousedown', (event) => event.preventDefault());
    this.clear.addEventListener('click', () => this.setQuery('', true));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.state.open) this.close();
    });
    document.addEventListener('mousedown', (event) => {
      // Clicks inside the quote dialog opened from the panel ("Gửi yêu cầu tìm hàng") are not outside clicks: closing
      // here would remove the button the dialog returns focus to.
      if (!this.state.open || this.form.contains(event.target) || this.sheet?.contains(event.target)
        || event.target?.closest?.('[data-quote-dialog]')) return;
      this.close();
    });
    this.scopeOptions.forEach((button) => {
      button.addEventListener('mousedown', () => {
        this.setScope(button.textContent.trim());
        this.queueSuggest();
      });
    });
  }

  isPhone() { return window.matchMedia('(max-width: 39.99rem)').matches; }
  scope() { return this.scopeLabel?.textContent?.trim() || SCOPE_ALL; }

  setScope(scope) {
    if (this.scopeLabel) this.scopeLabel.textContent = scope;
    this.scopeOptions.forEach((button) => button.classList.toggle('is-active', button.textContent.trim() === scope));
    const template = this.input.dataset.placeholderTemplate || '';
    if (template) this.input.placeholder = template.replace('[scope]', scope);
    this.renderScopeChips();
  }

  setQuery(value, focus = false) {
    this.state.query = value;
    this.input.value = value;
    if (this.sheetInput) this.sheetInput.value = value;
    this.clear.hidden = !value;
    if (focus) (this.sheetInput || this.input).focus();
    this.queueSuggest();
    this.render();
  }

  open() {
    if (this.isPhone()) { this.openSheet(); return; }
    if (!this.state.query.trim() && !this.hasFocusContent()) return;
    if (!this.state.open) {
      this.state.open = true;
      this.input.setAttribute('aria-expanded', 'true');
      this.backdrop = el('div', 'ptt-search-backdrop');
      document.body.append(this.backdrop);
    }
    this.render();
    if (this.state.query.trim()) this.queueSuggest();
  }

  close() {
    const shouldReturnFocus = this.state.open;
    this.state.open = false;
    this.state.sheet = false;
    this.state.active = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.backdrop?.remove();
    this.panel?.remove();
    this.sheet?.remove();
    this.restoreBackground();
    document.body.classList.remove('ptt-search-lock');
    this.abortController?.abort();
    if (shouldReturnFocus) this.input.focus({ preventScroll: true });
  }

  openSheet() {
    if (this.state.sheet) return;
    this.state.open = true;
    this.state.sheet = true;
    document.body.classList.add('ptt-search-lock');
    this.sheet = el('div', 'ptt-search-sheet', { role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Tìm kiếm' });
    const bar = el('div', 'ptt-search-sheet__bar');
    const back = el('button', 'ptt-search-sheet__back', { type: 'button', 'aria-label': 'Quay lại', text: '‹' });
    const inputWrap = el('div', 'ptt-search-sheet__input');
    this.sheetInput = el('input', '', {
      type: 'search', value: this.state.query, placeholder: 'Tìm dụng cụ, thương hiệu, mã hàng…',
      autocomplete: 'off', enterkeyhint: 'search', class: 'snize-exclude-input',
      role: 'combobox', 'aria-autocomplete': 'list', 'aria-expanded': 'true'
    });
    const sheetClear = el('button', 'ptt-search-sheet__clear', { type: 'button', 'aria-label': 'Xóa', text: '×' });
    const cancel = el('button', 'ptt-search-sheet__cancel', { type: 'button', text: 'Huỷ' });
    inputWrap.append(icon('search'), this.sheetInput, sheetClear);
    bar.append(back, inputWrap, cancel);
    this.sheetScopes = el('div', 'ptt-search-sheet__scopes');
    this.sheet.append(bar, this.sheetScopes);
    document.body.append(this.sheet);
    this.inertBackground();
    back.addEventListener('click', () => this.close());
    cancel.addEventListener('click', () => this.close());
    sheetClear.addEventListener('click', () => this.setQuery('', true));
    this.sheet.addEventListener('keydown', (event) => this.trapSheetFocus(event));
    this.sheetInput.addEventListener('input', () => {
      this.input.value = this.sheetInput.value;
      this.setQuery(this.sheetInput.value);
    });
    this.sheetInput.addEventListener('keydown', (event) => {
      if (this.keydown(event)) return;
      if (event.key === 'Enter') this.submit();
    });
    this.renderScopeChips();
    this.render();
    this.queueSuggest();
    setTimeout(() => this.sheetInput.focus(), 30);
  }

  inertBackground() {
    this.restoreBackground();
    this.inertSiblings = [...document.body.children].filter((node) => node !== this.sheet && !isQuoteDialog(node)).map((node) => ({
      node,
      inert: node.inert,
      ariaHidden: node.getAttribute('aria-hidden')
    }));
    this.inertSiblings.forEach(({ node }) => {
      node.inert = true;
      node.setAttribute('aria-hidden', 'true');
    });
  }

  restoreBackground() {
    this.inertSiblings.forEach(({ node, inert, ariaHidden }) => {
      node.inert = Boolean(inert);
      if (ariaHidden === null) node.removeAttribute('aria-hidden');
      else node.setAttribute('aria-hidden', ariaHidden);
    });
    this.inertSiblings = [];
  }

  trapSheetFocus(event) {
    if (event.key !== 'Tab' || !this.sheet) return false;
    if (!this.sheet.contains(document.activeElement)) {
      const activeDialog = document.querySelector('[data-quote-dialog]:not([hidden])');
      if (activeDialog?.contains(document.activeElement)) return false;
    }
    const focusables = Array.from(this.sheet.querySelectorAll(focusableSelector))
      .filter((node) => !node.hidden && !node.hasAttribute('disabled'));
    if (!focusables.length) {
      event.preventDefault();
      return true;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return true;
    }
    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
      return true;
    }
    if (!this.sheet.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
      return true;
    }
    return false;
  }

  renderScopeChips() {
    if (!this.sheetScopes) return;
    this.sheetScopes.replaceChildren();
    this.scopeOptions.forEach((option, index) => {
      const value = option.textContent.trim();
      const button = el('button', index === 0 ? 'is-active' : '', { type: 'button', text: index === 0 ? 'Tất cả' : value });
      button.classList.toggle('is-active', value === this.scope());
      button.addEventListener('click', () => { this.setScope(value); this.queueSuggest(); });
      this.sheetScopes.append(button);
    });
  }

  queueSuggest() {
    clearTimeout(this.timer);
    const query = this.state.query.trim();
    this.pendingStartedAt = performance.now();
    if (!query) { this.state.data = null; this.render(); return; }
    const started = this.pendingStartedAt;
    this.timer = setTimeout(() => this.load(query, started), DEBOUNCE_MS);
  }

  async load(query, started = performance.now()) {
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    try {
      const data = await suggest(query, { scope: this.scope(), signal: controller.signal, config: this.config });
      if (controller.signal.aborted || query !== this.state.query.trim()) return;
      this.metrics.requestCount += data.requestCount || 0;
      this.state.data = data;
      this.render(started);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.state.data = { keywords: [], brands: [], collections: [], articles: [], products: [], total: 0, error: true };
        this.render();
      }
    }
  }

  keydown(event) {
    if (!this.state.open) return false;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.state.active = this.actions.length ? (this.state.active + 1) % this.actions.length : -1;
      this.syncActive();
      return true;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.state.active = this.actions.length ? (this.state.active <= 0 ? this.actions.length - 1 : this.state.active - 1) : -1;
      this.syncActive();
      return true;
    }
    if (event.key === 'Enter' && this.state.active > -1 && this.actions[this.state.active]) {
      event.preventDefault();
      this.actions[this.state.active].run();
      return true;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return true;
    }
    return false;
  }

  syncActive() {
    this.actions.forEach(({ node }, index) => {
      const active = index === this.state.active;
      node.classList.toggle('is-active', active);
      node.setAttribute('aria-selected', String(active));
      if (active) {
        const id = node.id || `SearchOption-${Date.now()}-${this.state.active}`;
        node.setAttribute('id', id);
        (this.sheetInput || this.input).setAttribute('aria-activedescendant', id);
      }
    });
  }

  action(node, run) {
    const index = this.actions.push({ node, run }) - 1;
    node.dataset.searchOption = String(index);
    node.setAttribute('role', 'option');
    node.setAttribute('aria-selected', 'false');
    node.addEventListener('mouseenter', () => { this.state.active = index; this.syncActive(); });
    node.addEventListener('mousedown', (event) => event.preventDefault());
    node.addEventListener('click', run);
    return node;
  }

  pick(term) {
    this.setQuery(term);
    recordSearch(term);
    this.submit(term);
  }

  submit(term = this.state.query) {
    const clean = String(term || '').trim();
    if (!clean) return;
    recordSearch(clean);
    window.location.href = searchUrl(this.config, clean);
  }

  section(title, body, right = '') {
    if (!body || !body.childNodes.length) return null;
    const section = el('section', 'ptt-search-panel__section');
    const head = el('div', 'ptt-search-panel__head');
    head.append(el('span', '', { text: title }));
    if (right) head.append(typeof right === 'string' ? el('small', '', { text: right }) : right);
    section.append(head, body);
    return section;
  }

  keywordRows(list, removable = false, trend = false) {
    const body = el('div', '', { role: 'listbox' });
    list.forEach((term, index) => {
      const row = removable ? el('div', 'ptt-search-panel__row') : el('button', 'ptt-search-panel__row', { type: 'button' });
      if (trend) {
        row.append(el('span', `ptt-search-panel__rank${index < 3 ? ' is-hot' : ''}`, { text: String(index + 1) }));
      } else row.append(icon(removable ? '↺' : '⌕'));
      row.append(el('span', '', { html: highlight(typeof term === 'string' ? term : term.title, this.state.query, { strong: !removable && !trend }) }));
      if (trend && index < 3) row.append(el('span', 'ptt-search-panel__trend', { text: '↗' }));
      if (removable) {
        const remove = el('button', 'ptt-search-panel__remove', { type: 'button', 'aria-label': `Xoá ${term}`, text: '×' });
        remove.addEventListener('mousedown', (event) => {
          event.preventDefault();
          event.stopPropagation();
        });
        remove.addEventListener('click', (event) => {
          event.stopPropagation();
          this.state.recent = this.state.recent.filter((item) => item !== term);
          saveRecent(this.state.recent);
          this.render();
        });
        row.append(remove);
      }
      this.action(row, () => this.pick(typeof term === 'string' ? term : term.title));
      body.append(row);
    });
    return body;
  }

  brandRows(brands) {
    const body = el('div', '', { role: 'listbox' });
    brands.forEach((brand) => {
      const row = el('button', 'ptt-search-panel__brand', { type: 'button' });
      const logo = el('span', 'ptt-search-panel__brand-logo');
      if (brand.logo) logo.append(el('img', '', { src: brand.logo, alt: '' }));
      else logo.append(el('b', '', { text: brand.name }));
      const copy = el('span');
      copy.append(el('span', 'ptt-search-panel__brand-name', { text: brand.name }));
      copy.append(el('small', '', { text: `Nhà phân phối chính hãng${brand.productsCount ? ` · ${brand.productsCount} sản phẩm` : ''}` }));
      row.append(logo, copy, icon('›'));
      this.action(row, () => { recordSearch(brand.name); window.location.href = brand.url || searchUrl(this.config, brand.name); });
      body.append(row);
    });
    return body;
  }

  categoryRows(categories) {
    const body = el('div', '', { role: 'listbox' });
    categories.forEach((category) => {
      const row = el('button', 'ptt-search-panel__category', { type: 'button' });
      const copy = el('span');
      copy.append(el('span', '', { html: highlight(category.title, this.state.query) }));
      row.append(icon('search'), copy);
      this.action(row, () => { window.location.href = category.url || searchUrl(this.config, category.title); });
      body.append(row);
    });
    return body;
  }

  articleRows(articles) {
    const body = el('div', '', { role: 'listbox' });
    articles.forEach((article) => {
      const row = el('button', 'ptt-search-panel__row', { type: 'button' });
      row.append(icon('▧'), el('span', '', { html: highlight(article.title, this.state.query) }));
      this.action(row, () => { window.location.href = article.url; });
      body.append(row);
    });
    return body;
  }

  productRows(products, skuHit = null) {
    const body = el('div', '', { role: 'listbox' });
    products.forEach((product) => body.append(this.productRow(product, product === skuHit)));
    return body;
  }

  productRow(product, big = false) {
    const row = el('button', `ptt-search-panel__product${big ? ' is-sku' : ''}`, { type: 'button' });
    const image = el('span', 'ptt-search-panel__product-image');
    if (product.image) image.append(el('img', '', { src: product.image, alt: '', loading: 'lazy', width: big ? '76' : '60', height: big ? '76' : '60' }));
    const info = el('span', 'ptt-search-panel__product-info');
    const kicker = el('span', 'ptt-search-panel__product-kicker');
    kicker.append(el('span', 'ptt-search-panel__product-vendor', { text: product.vendor || '' }));
    if (product.sku) kicker.append(el('span', 'ptt-search-panel__sku', { html: highlight(product.sku, this.state.query) }));
    const title = el('span', 'ptt-search-panel__title');
    if (product.isNew) title.append(el('span', 'ptt-search-panel__new', { text: 'Mới' }), document.createTextNode(' '));
    title.insertAdjacentHTML('beforeend', highlight(product.title, this.state.query));
    const price = el('span', 'ptt-search-panel__product-price-row');
    price.append(el('b', 'ptt-search-panel__price', { text: money(product, 'price', 'priceText') }));
    if (product.compareAtPrice > product.price) price.append(el('s', 'ptt-search-panel__compare', { text: money(product, 'compareAtPrice', 'compareAtPriceText') }));
    if (product.discount > 0) price.append(el('span', 'ptt-search-panel__discount', { text: `-${product.discount}%` }));
    price.append(el('span', 'ptt-search-panel__stock', { text: productStockLabel(product.stockState), 'data-state': product.stockState || 'in-stock' }));
    info.append(kicker, title, price);
    row.append(image, info);
    this.action(row, () => { recordSearch(this.state.query); window.location.href = product.url; });
    return row;
  }

  focusState() {
    const left = document.createDocumentFragment();
    if (this.state.recent.length) {
      const clearAll = el('button', 'ptt-search-panel__clear-recent', { type: 'button', text: 'Xoá tất cả' });
      clearAll.addEventListener('mousedown', (event) => event.preventDefault());
      clearAll.addEventListener('click', () => {
        this.state.recent = [];
        saveRecent([]);
        this.render();
      });
      left.append(this.section('Tìm kiếm gần đây', this.keywordRows(this.state.recent, true), clearAll));
    }
    if (this.config.trendingTerms?.length) left.append(this.section('Xu hướng tìm kiếm', this.keywordRows(this.config.trendingTerms.slice(0, 6), false, true)));
    const right = document.createDocumentFragment();
    if (this.config.featuredBrands?.length) {
      const grid = el('div', 'ptt-search-brand-grid', { role: 'listbox' });
      this.config.featuredBrands.slice(0, 8).forEach((brand) => {
        const button = el('button', '', { type: 'button', 'aria-label': brand.name });
        if (brand.logo) button.append(el('img', '', { src: brand.logo, alt: '' }));
        else button.textContent = brand.name;
        this.action(button, () => { window.location.href = brand.url || searchUrl(this.config, brand.name); });
        grid.append(button);
      });
      right.append(this.section('Thương hiệu chính hãng', grid));
    }
    if (this.config.categories?.length) {
      const chips = el('div', 'ptt-search-category-chips', { role: 'listbox' });
      this.config.categories.slice(0, 8).forEach((category) => {
        const button = el('button', '', { type: 'button' });
        if (category.image) button.append(el('img', '', { src: category.image, alt: '', width: '28', height: '28' }));
        button.append(document.createTextNode(category.title));
        this.action(button, () => { window.location.href = category.url; });
        chips.append(button);
      });
      right.append(this.section('Danh mục phổ biến', chips));
    }
    return [left, right, false];
  }

  resultsState(data) {
    const left = document.createDocumentFragment();
    if (data.keywords?.length) left.append(this.section('Gợi ý từ khoá', this.keywordRows(data.keywords.map((item) => item.title))));
    if (data.brands?.length) left.append(this.section('Thương hiệu', this.brandRows(data.brands)));
    if (data.collections?.length) left.append(this.section('Danh mục', this.categoryRows(data.collections)));
    if (data.articles?.length) left.append(this.section('Bài viết & hướng dẫn', this.articleRows(data.articles)));
    const right = document.createDocumentFragment();
    const rest = data.products.filter((product) => !data.skuHit || product.id !== data.skuHit.id).slice(0, data.skuHit ? 3 : 4);
    if (data.skuHit) right.append(this.section('Khớp mã hàng', this.productRows([data.skuHit], data.skuHit)));
    if (rest.length) right.append(this.section('Sản phẩm', this.productRows(rest), `${data.total || rest.length} kết quả${this.scope() !== SCOPE_ALL ? ` trong ${this.scope()}` : ''}`));
    return [left, right, !left.childNodes.length];
  }

  emptyState(data) {
    const left = el('div', '');
    left.append(el('h2', 'ptt-search-panel__empty-title', { text: `Không tìm thấy “${this.state.query}”` }));
    const tips = el('ul', 'ptt-search-panel__tips');
    ['Kiểm tra lại chính tả hoặc bỏ bớt từ khoá', 'Thử tìm theo mã hàng, ví dụ LN46ACF', 'Tìm theo thương hiệu: KNIPEX, WERA, Snap-On…'].forEach((tip) => tips.append(el('li', '', { text: tip })));
    const sourcing = el('div', 'ptt-search-sourcing');
    sourcing.append(el('strong', '', { text: 'Chưa có trên web? Chúng tôi tìm giúp bạn' }));
    sourcing.append(el('p', '', { text: 'Gửi mã hàng hoặc ảnh sản phẩm, kỹ thuật viên báo giá trong giờ làm việc.' }));
    const actions = el('div', 'ptt-search-sourcing__actions');
    const source = el('button', '', { type: 'button' });
    source.append(icon('file-search'), document.createTextNode('Gửi yêu cầu tìm hàng'));
    source.addEventListener('click', () => openSourcingRequest(this.state.query));
    actions.append(source);
    if (this.config.zaloUrl) {
      const zalo = el('a', '', { href: this.config.zaloUrl, target: '_blank', rel: 'noopener noreferrer' });
      zalo.append(icon('message-circle'), document.createTextNode('Chat Zalo'));
      actions.append(zalo);
    }
    sourcing.append(actions);
    left.append(tips, sourcing);
    const rightProducts = (this.config.popularProducts || []).slice(0, 3);
    const right = document.createDocumentFragment();
    const popularSection = this.section('Khách hàng thường tìm', this.productRows(rightProducts));
    if (popularSection) right.append(popularSection);
    return [left, right, false];
  }

  hasFocusContent() {
    return Boolean(
      this.state.recent.length ||
      this.config.trendingTerms?.length ||
      this.config.featuredBrands?.length ||
      this.config.categories?.length
    );
  }

  render(started = 0) {
    if (!this.state.open) return;
    const renderStart = performance.now();
    this.actions = [];
    const query = this.state.query.trim();
    const data = this.state.data || {};
    let parts;
    let mode = 'focus';
    if (!query && !this.hasFocusContent()) {
      this.panel?.remove();
      if (!this.state.sheet) {
        this.backdrop?.remove();
        this.state.open = false;
        this.input.setAttribute('aria-expanded', 'false');
      }
      return;
    }
    if (!query) parts = this.focusState();
    else if (!data.products && !data.error) { parts = [el('div', '', { text: 'Đang tìm…' }), document.createDocumentFragment(), true]; mode = 'loading'; }
    else if (!(data.total || 0) && !data.keywords?.length && !data.brands?.length && !data.collections?.length && !data.articles?.length) { parts = this.emptyState(data); mode = 'empty'; }
    else { parts = this.resultsState(data); mode = 'results'; }
    const [left, right, leftEmpty] = parts;
    const rightEmpty = !right?.childNodes?.length;
    this.panel?.remove();
    this.panel = el('div', `ptt-search-panel is-${mode}${this.compact() ? ' is-compact' : ''}${leftEmpty ? ' is-left-empty' : ''}${rightEmpty ? ' is-right-empty' : ''}`, { role: 'dialog', 'aria-label': 'Gợi ý tìm kiếm' });
    const body = el('div', 'ptt-search-panel__body');
    const leftCol = leftEmpty ? null : el('div', 'ptt-search-panel__col ptt-search-panel__col--left');
    if (leftCol) leftCol.append(left);
    const rightCol = rightEmpty ? null : el('div', 'ptt-search-panel__col ptt-search-panel__col--right');
    if (rightCol) rightCol.append(right);
    if (this.compact() && mode === 'results' && leftCol) {
      if (rightCol) body.append(rightCol);
      body.append(leftCol);
    }
    else {
      if (leftCol) body.append(leftCol);
      if (rightCol) body.append(rightCol);
    }
    this.panel.append(body, this.footer(data));
    if (this.state.sheet) this.sheet.append(this.panel);
    else this.anchor.append(this.panel);
    this.state.active = -1;
    this.syncActive();
    const renderTime = Math.round((performance.now() - renderStart) * 10) / 10;
    this.metrics.renderTimes.push(renderTime);
    if (started) this.metrics.lastLatency = Math.round((performance.now() - started) * 10) / 10;
    this.form.dataset.searchRequestCount = String(this.metrics.requestCount);
    this.form.dataset.searchRenderTime = String(started ? this.metrics.lastLatency : renderTime);
  }

  compact() {
    return this.state.sheet || this.form.getBoundingClientRect().width < 720;
  }

  footer(data) {
    const footer = el('div', 'ptt-search-panel__footer');
    const keys = el('span', 'ptt-search-panel__keys');
    keys.innerHTML = '<span><kbd>↑</kbd> <kbd>↓</kbd> chọn</span><span><kbd>Enter</kbd> mở</span><span><kbd>Esc</kbd> đóng</span>';
    footer.append(keys);
    if (this.state.query.trim() && data.total) {
      const button = el('button', 'ptt-search-panel__see-all', { type: 'button', text: this.state.sheet ? `Xem tất cả ${data.total} kết quả →` : `Xem tất cả ${data.total} sản phẩm cho “${this.state.query.trim()}” →` });
      this.action(button, () => this.submit());
      footer.append(button);
    }
    return footer;
  }
}

const init = () => document.querySelectorAll('[data-scoped-search]').forEach((form) => {
  if (!form.dataset.searchPanelReady) {
    form.dataset.searchPanelReady = 'true';
    form.searchPanel = new SearchPanel(form);
  }
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();

if (typeof window !== 'undefined') window.PTT_SearchSuggest = { recordSearch };
export { SearchPanel, highlight };
